<template>
    <main class="flex-1 overflow-y-auto focus:outline-none">
        <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <h1 class="text-2xl font-semibold text-gray-900">Zoznam FAQ</h1>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="py-4">
                    <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div class="px-4 py-5 sm:px-6 flex justify-between">
                            <div>
                                <h3 class="text-lg leading-6 font-medium text-gray-900">Často kladené otázky</h3>
                                <p class="mt-1 max-w-2xl text-sm text-gray-500">Správa a prehľad FAQ pre zákazníkov</p>
                            </div>
                            <div>
                                <button @click="goToAddFaq"
                                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                    Pridať FAQ
                                </button>
                            </div>
                        </div>
                        <div v-if="loading" class="text-center py-4">
                            <div
                                class="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-solid border-gray-300 border-t-teal-500">
                            </div>
                            <p class="mt-2 text-sm text-gray-500">Načítavanie...</p>
                        </div>
                        <div v-else-if="faqs.length === 0" class="text-center py-8">
                            <p class="text-gray-500">Žiadne FAQ neboli nájdené</p>
                            <button @click="goToAddFaq"
                                class="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                Pridať prvé FAQ
                            </button>
                        </div>
                        <div v-else class="overflow-hidden">
                            <ul role="list" class="divide-y divide-gray-200">
                                <li v-for="faq in faqs" :key="faq.id" class="px-6 py-4 hover:bg-gray-50">
                                    <div class="flex items-center justify-between">
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center">
                                                <span
                                                    class="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-1 mr-2">
                                                    #{{ faq.position || '—' }}
                                                </span>
                                                <p class="text-sm font-medium text-gray-900 truncate">{{ faq.title }}
                                                </p>
                                            </div>
                                            <p class="mt-1 text-sm text-gray-500 line-clamp-2">{{ faq.content }}</p>
                                        </div>
                                        <div class="ml-4 flex-shrink-0 flex space-x-2">
                                            <button @click="goToDetailFaq(faq.id)"
                                                class="bg-white rounded-md font-medium text-teal-600 hover:text-teal-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                                Detail
                                            </button>
                                            <button @click="goToEditFaq(faq.id)"
                                                class="bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                                Upraviť
                                            </button>
                                            <button @click="deleteFaq(faq.id)"
                                                class="bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                                Vymazať
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axiosClient from '../axios'
import type { Faq } from '../types'

const router = useRouter()
const faqs = ref<Faq[]>([])
const loading = ref(true)

onMounted(async () => {
    await fetchFaqs()
})

async function fetchFaqs() {
    loading.value = true
    try {
        const response = await axiosClient.get('/faqs/getAllFaqs')
        if (response.data && response.data.success) {
            // Handle only the new format (direct array)
            faqs.value = response.data.data || []
        }
    } catch (error) {
        console.error('Error fetching FAQs:', error)
    } finally {
        loading.value = false
    }
}

function goToAddFaq() {
    router.push({ name: 'Pridať FAQ' })
}

function goToDetailFaq(id: number) {
    router.push({ name: 'Detail FAQ', params: { id: id.toString() } })
}

function goToEditFaq(id: number) {
    router.push({ name: 'Upraviť FAQ', params: { id: id.toString() } })
}

async function deleteFaq(id: number) {
    if (!confirm('Naozaj chcete vymazať túto FAQ?')) {
        return
    }

    try {
        const response = await axiosClient.delete(`/faqs/${id}/delete`)
        if (response.data && response.data.success) {
            await fetchFaqs()
        }
    } catch (error) {
        console.error('Error deleting FAQ:', error)
    }
}
</script>

<!-- Styles have been moved to global CSS file -->
